import React from 'react';
import type {
  NurseBandDayTypeResolvedRate,
  NurseBandPositionResolvedRates,
  NurseBandResolvedRates,
} from '../services/api';

interface NurseBandingResolvedRatesPanelProps {
  rates: NurseBandResolvedRates | null;
  /** When set, show only these position blocks (e.g. employee main + sub positions). */
  filterPositionIds?: number[];
  /** @deprecated Prefer filterPositionIds — single-position filter. */
  filterPositionId?: number | null;
}

function formatMoney(v: number): string {
  return Number(v).toFixed(2);
}

function baseRateForDay(baseRates: NurseBandDayTypeResolvedRate[], ...labels: string[]): string {
  const normalized = labels.map((l) => l.toLowerCase().replace(/\s+/g, ''));
  const row = baseRates.find((d) => normalized.includes(d.dayType.toLowerCase().replace(/\s+/g, '')));
  return row != null ? formatMoney(row.baseRate) : '—';
}

function PositionRatesBlock({ block }: { block: NurseBandPositionResolvedRates }) {
  const baseRates = block.baseRates ?? [];
  const additionalRows = (block.schedules ?? []).filter((s) => s.hasRateCard1Match && s.additionalHourlyRate > 0);

  const tableClass = 'min-w-full text-sm border-collapse';
  const thClass = 'px-3 py-2 text-left text-xs font-medium text-neutral-600 border border-neutral-200 bg-neutral-50';
  const tdClass = 'px-3 py-2 text-neutral-800 border border-neutral-200 font-mono';

  if (block.bandYear == null) {
    return (
      <p className="text-sm text-neutral-500">
        No band year effective for {block.positionName}.
      </p>
    );
  }

  if (baseRates.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No position banding rate table mapped for {block.positionName}.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className={tableClass}>
          <thead>
            <tr>
              <th className={thClass}>BY</th>
              <th className={thClass}>Mon–Fri</th>
              <th className={thClass}>Saturday</th>
              <th className={thClass}>Sunday</th>
              <th className={thClass}>Bank holiday</th>
              <th className={thClass}>Midweek red eye</th>
              <th className={thClass}>Weekend red eye</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={`${tdClass} font-sans font-medium`}>{block.bandYear}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Mon–Fri', 'Mon - Fri')}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Saturday')}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Sunday')}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Bank holiday', 'Bank Holiday')}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Midweek red eye')}</td>
              <td className={tdClass}>{baseRateForDay(baseRates, 'Weekend red eye')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {additionalRows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>BY</th>
                <th className={thClass}>Schedule</th>
                <th className={thClass}>Additional €/hr</th>
              </tr>
            </thead>
            <tbody>
              {additionalRows.map((row) => (
                <tr key={row.scheduleId}>
                  <td className={`${tdClass} font-sans font-medium`}>{block.bandYear}</td>
                  <td className={`${tdClass} font-sans`}>{row.scheduleName}</td>
                  <td className={tdClass}>{formatMoney(row.additionalHourlyRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-neutral-500">No schedule additional rates for this position.</p>
      )}
    </div>
  );
}

const NurseBandingResolvedRatesPanel: React.FC<NurseBandingResolvedRatesPanelProps> = ({
  rates,
  filterPositionIds,
  filterPositionId,
}) => {
  if (rates?.bandYear == null && (rates?.positions?.length ?? 0) === 0) {
    return (
      <p className="text-neutral-500 text-sm mt-2">
        Configure band year history below to resolve rates.
      </p>
    );
  }

  const positionBlocks =
    rates?.positions && rates.positions.length > 0
      ? rates.positions
      : rates
        ? [
            {
              positionId: 0,
              positionName: 'Main position',
              rateCardId: rates.rateCardId,
              rateCardName: null,
              bandYear: rates.bandYear,
              baseRates: rates.baseRates ?? [],
              schedules: rates.schedules ?? [],
            },
          ]
        : [];

  const positionIdFilter =
    filterPositionIds != null && filterPositionIds.length > 0
      ? new Set(filterPositionIds.filter((id) => id > 0))
      : filterPositionId != null && filterPositionId > 0
        ? new Set([filterPositionId])
        : null;

  const visibleBlocks =
    positionIdFilter != null
      ? positionBlocks.filter((p) => positionIdFilter.has(p.positionId))
      : positionBlocks;

  if (visibleBlocks.length === 0) {
    return (
      <p className="text-neutral-500 text-sm mt-2">
        No banding rates for the employee&apos;s position{positionIdFilter && positionIdFilter.size > 1 ? 's' : ''}.
      </p>
    );
  }

  return (
    <div className="space-y-5 mt-3">
      {visibleBlocks.map((block) => (
        <div
          key={block.positionId || block.positionName}
          className="rounded-md border border-neutral-200 bg-neutral-50/60 px-3 py-3"
        >
          <div className="mb-2">
            <p className="font-medium text-neutral-900">{block.positionName}</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {block.rateCardName
                ? `Rate table: ${block.rateCardName}`
                : 'No rate table mapped for this position'}
              {block.bandYear != null ? ` · Band year ${block.bandYear}` : ''}
            </p>
          </div>
          <PositionRatesBlock block={block} />
        </div>
      ))}
    </div>
  );
};

export default NurseBandingResolvedRatesPanel;
